import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  type UIEvent,
} from 'react';

type ScrollEdge = 'none' | 'start' | 'middle' | 'end';

/**
 * Which edge a horizontally scrolling element is against, as a `data-scroll`
 * value the stylesheet turns into edge fades: `none` (everything fits),
 * `start`, `middle`, `end`. Listens passively and coalesces to one frame.
 */
export function useScrollAffordance(ref: RefObject<HTMLElement>): ScrollEdge {
  const [edge, setEdge] = useState<ScrollEdge>('none');
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const max = el.scrollWidth - el.clientWidth;
      const next: ScrollEdge =
        max <= 1 ? 'none' : el.scrollLeft <= 1 ? 'start' : el.scrollLeft >= max - 1 ? 'end' : 'middle';
      setEdge((current) => (current === next ? current : next));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    measure();
    el.addEventListener('scroll', schedule, { passive: true });
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
    observer?.observe(el);
    return () => {
      el.removeEventListener('scroll', schedule);
      observer?.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [ref]);
  return edge;
}

/**
 * Two scrollers that follow each other sideways — a metrics table and its
 * totals bar. Each side writes the other's `scrollLeft` only when it actually
 * differs, so the pair settles instead of ping-ponging.
 */
export function useLinkedScroll() {
  const a = useRef<HTMLDivElement>(null);
  const b = useRef<HTMLDivElement>(null);
  const follow = (other: RefObject<HTMLDivElement>) => (event: UIEvent<HTMLDivElement>) => {
    const target = other.current;
    if (target && Math.abs(target.scrollLeft - event.currentTarget.scrollLeft) > 1) {
      target.scrollLeft = event.currentTarget.scrollLeft;
    }
  };
  return {
    primary: { ref: a, onScroll: follow(b) },
    secondary: { ref: b, onScroll: follow(a) },
  };
}

export interface ScrollTableProps {
  /** Accessible name of the region. */
  label: string;
  /** Which column stays pinned while the rest scrolls: the first or the second. */
  identityColumn?: 1 | 2;
  className?: string;
  style?: CSSProperties;
  scrollRef?: RefObject<HTMLDivElement>;
  onScroll?: (event: UIEvent<HTMLDivElement>) => void;
  children: ReactNode;
}

/**
 * The one way a wide table scrolls sideways. A labelled, keyboard-scrollable
 * region (arrow keys move it, since WebKit touch contexts do not reliably
 * scroll a focused region), an optional pinned identity column, and edge
 * fades that appear only when there is more to see. Used at every width: a
 * table that fits shows no fade and behaves like a plain container.
 */
export function ScrollTable({
  label,
  identityColumn,
  className,
  style,
  scrollRef,
  onScroll,
  children,
}: ScrollTableProps) {
  const own = useRef<HTMLDivElement>(null);
  const ref = scrollRef ?? own;
  const edge = useScrollAffordance(ref);
  const onKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      event.currentTarget.scrollLeft += event.key === 'ArrowRight' ? 120 : -120;
    }
  }, []);
  return (
    <div className="scroll-table-frame" data-scroll={edge}>
      <div
        ref={ref}
        className={`scroll-table${className ? ` ${className}` : ''}`}
        role="region"
        aria-label={label}
        tabIndex={0}
        data-identity-column={identityColumn}
        style={style}
        onScroll={onScroll}
        onKeyDown={onKeyDown}
      >
        {children}
      </div>
    </div>
  );
}
